import { SimpleTable } from '@app/components/base/SimpleTable';
import { RowActionMui } from '@app/components/table/RowActionMui';
import { AuditAudio } from '@mi-front/core';
import CloseIcon from '@mui/icons-material/Close';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';

const showCustomDataMessage = defineMessage({
  id: 'audit.audiosScreen.showCustomData',
  defaultMessage: 'Mostrar Custom data',
});

const customDataTitleMessage = defineMessage({
  id: 'audit.audiosScreen.customDataTitle',
  defaultMessage: 'Custom data de {customId}',
});

const customDataNameMessage = defineMessage({
  id: 'audit.audiosScreen.customData.name',
  defaultMessage: 'Nombre',
});

const customDataValueMessage = defineMessage({
  id: 'audit.audiosScreen.customData.value',
  defaultMessage: 'Valor',
});

/**
 * Formats a custom-data cell value for display (strings as-is, others as JSON).
 */
function formatCustomDataValue(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (value === undefined) {
    return 'undefined';
  }

  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}

/**
 * Opens a dialog with one row for each custom data field.
 * Renders nothing when `customData` is missing or empty.
 */
export function ShowCustomDataAction({
  customId,
  customData,
}: {
  customId: AuditAudio['customId'];
  customData: Record<string, unknown> | null | undefined;
}) {
  const { $t } = useIntl();
  const [open, setOpen] = useState(false);

  if (!customData || Object.keys(customData).length === 0) {
    return null;
  }

  const data = Object.entries(customData).map(([name, value]) => ({ name, value }));

  return (
    <>
      <RowActionMui title={$t(showCustomDataMessage)} onClick={() => setOpen(true)}>
        <DataObjectIcon />
      </RowActionMui>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DataObjectIcon />
          <FormattedMessage {...customDataTitleMessage} values={{ customId }} />
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={() => setOpen(false)} sx={{ my: 0 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          <SimpleTable
            columns={[
              {
                header: <FormattedMessage {...customDataNameMessage} />,
                renderCell: (row) => row.name,
              },
              {
                header: <FormattedMessage {...customDataValueMessage} />,
                renderCell: (row) => (
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                  >
                    {formatCustomDataValue(row.value)}
                  </Typography>
                ),
              },
            ]}
            data={data}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
